import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box, 
  Menu, 
  MenuItem, 
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  Divider
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Language as LanguageIcon, 
  WhatsApp as WhatsAppIcon 
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../utils/LanguageContext';
import contactInfo from '../../config/contact';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Language menu state
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const isLangMenuOpen = Boolean(langAnchorEl);
  
  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Handle language menu
  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };
  
  const handleLanguageMenuClose = () => {
    setLangAnchorEl(null);
  };
  
  const handleLanguageChange = (lang: string) => {
    changeLanguage(lang);
    handleLanguageMenuClose();
  };
  
  // Handle mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Handle quick order click - scroll to section on current page or home page
  const handleQuickOrderClick = () => {
    // Try to find quick order section on current page
    const element = document.getElementById('quick-order-form');
    if (element) {
      // If found on current page, just scroll to it
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If not found, navigate to home page and then scroll
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const homeElement = document.getElementById('quick-order-form');
        if (homeElement) {
          homeElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  
  // WhatsApp link
  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.number}?text=${encodeURIComponent(contactInfo.whatsapp.message[language as keyof typeof contactInfo.whatsapp.message])}`;
  
  // Navigation items
  const navItems = [
    { label: t('nav.home'), path: '/', type: 'link' },
    { label: t('nav.services'), path: '/services', type: 'link' },
    { label: t('nav.quickOrder'), path: '/quick-order', type: 'quickOrder' },
    { label: t('nav.faq'), path: '/faq', type: 'link' },
    { label: t('nav.contact'), path: '/contact', type: 'link' },
  ];
  
  // Mobile drawer content
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', pt: 2, pb: 2 }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        {contactInfo.company.name[language as keyof typeof contactInfo.company.name]}
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            {item.type === 'quickOrder' ? (
              <Box 
                onClick={() => {
                  handleDrawerToggle();
                  handleQuickOrderClick();
                }}
                sx={{ 
                  width: '100%',
                  textAlign: 'center', 
                  color: 'white', 
                  py: 1,
                  cursor: 'pointer'
                }}
              >
                {item.label}
              </Box>
            ) : (
              <Box 
                component={Link} 
                to={item.path} 
                sx={{ 
                  width: '100%',
                  textAlign: 'center', 
                  color: 'white', 
                  py: 1 
                }}
              >
                {item.label}
              </Box>
            )}
          </ListItem>
        ))}
        <ListItem disablePadding>
          <Box
            component="a" 
            href={whatsappUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              width: '100%',
              textAlign: 'center', 
              color: 'white', 
              py: 1 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WhatsAppIcon sx={{ mr: 1 }} />
              WhatsApp
            </Box>
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              mr: isMobile ? 0 : 5,
            }}
          >
            <Typography variant="h6">
              <Box 
                component="img" 
                src="/Full Logo White.png" 
                alt={contactInfo.company.name[language as keyof typeof contactInfo.company.name]} 
                sx={{ 
                  height: 40, 
                  mr: 1, 
                  display: { xs: 'none', sm: 'block' },
                  p: 0.7,
                  objectFit: 'contain'
                }} 
              />
            </Typography>
          </Box>
          
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex' }}>
              {navItems.map((item) => (
                item.type === 'quickOrder' ? (
                  <Box
                    key={item.path}
                    onClick={handleQuickOrderClick}
                    sx={{ 
                      mx: 1,
                      color: 'inherit',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                      px: 2,
                      cursor: 'pointer'
                    }}
                  >
                    {item.label}
                  </Box>
                ) : (
                  <Box
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{ 
                      mx: 1,
                      color: 'inherit',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                      px: 2
                    }}
                  >
                    {item.label}
                  </Box>
                )
              ))}
            </Box>
          )}
          
          <Box sx={{ display: 'flex' }}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              color="inherit"
              aria-label="language"
              aria-controls="language-menu"
              aria-haspopup="true"
            >
              <LanguageIcon />
            </IconButton>
            
            <Menu
              id="language-menu"
              anchorEl={langAnchorEl}
              open={isLangMenuOpen}
              onClose={handleLanguageMenuClose}
              MenuListProps={{
                'aria-labelledby': 'language-button',
              }}
            >
              <MenuItem 
                onClick={() => handleLanguageChange('zh')} 
                selected={language === 'zh'}
              >
                {t('language.zh')}
              </MenuItem>
              <MenuItem 
                onClick={() => handleLanguageChange('en')} 
                selected={language === 'en'}
              >
                {t('language.en')}
              </MenuItem>
            </Menu>
            
            {!isMobile && (
              <Box
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  ml: 2,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  textDecoration: 'none',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  }
                }}
              >
                <WhatsAppIcon sx={{ mr: 1 }} />
                WhatsApp
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header; 